/**
 * TerrainRouten — Routen als flache Pfad-Baender auf dem Terrain.
 *
 * Verfahren = Alex' Blender-Skript MeshLineToPlane.py, engine-seitig:
 * Polyline hochaufloesend resampeln, jeden Stuetzpunkt per
 * TerrainRoot.getWorldY nach unten aufs Gelaende projizieren, dann je
 * Segment orthogonal (XZ-Ebene) um thickness/2 nach links/rechts
 * versetzen und einen Quad-Strip bauen. Miter durch Mittelung der
 * Nachbar-Orthogonalen — wie im Skript, nur fortlaufend statt pro Kante.
 * (Die GLB-Karte macht dasselbe offline: GPX → Snap → Curve mit
 * bevel_depth 2.0 — PlaceGpxSections.py; hier live gegen die Heightmaps.)
 *
 * Da die Kachel-Hoehen streamen, wird jedes Band bis zu N-mal neu
 * gedrapt, sobald sich die Summe der Hoehen merklich aendert — danach
 * ist es statisch.
 */
import * as THREE from 'three';
import { SceneNode } from '../engine/core/SceneNode';
import type { TerrainRoot } from '../engine/terrain/TerrainRoot';
import type { BridgeRoute } from '../embed/MapBridge';

interface RouteDaten {
    id: number | string;
    name: string;
    farbe: string | null;
    punkte: [number, number][];       // [lng, lat]
}

interface Band {
    daten: RouteDaten;
    /** projizierte Stuetzpunkte in Weltkoordinaten (x/z fix, y wird gedrapt) */
    xz: { x: number; z: number }[];
    mesh: THREE.Mesh;
    letzteHoehenSumme: number;
    drapeVersuche: number;
    /** Stapel-Ebene: parallele Routen liegen uebereinander (Alex) */
    ebene: number;
}

const ROUTENFARBEN: Record<number, string> = { 56: '#2fbf71', 60: '#c9a44c', 63: '#7c3158', 65: '#2e718c' };

export interface TerrainRoutenConfig {
    terrain: TerrainRoot;
    datenUrl?: string;
    /** Bandbreite in Welt-Units (0.001 = 5 m bei 1u=5km) */
    breite?: number;
    /** Hochlage ueber Grund gegen Z-Fighting */
    hochlage?: number;
    /** Resample-Schrittweite in Welt-Units (0.002 = 10 m) */
    schritt?: number;
    /** vertikaler Abstand gestapelter Routen (0.0003 = 1,5 m) */
    stapelAbstand?: number;
    /** Standalone demo data. Embed renderers receive routes through MapBridge. */
    autoLoad?: boolean;
}

export class TerrainRouten extends SceneNode {
    private readonly _cfg: Required<TerrainRoutenConfig>;
    private readonly _terrain: TerrainRoot;
    private _baender: Band[] = [];
    private _drapeUhr = 0;

    constructor(cfg: TerrainRoutenConfig) {
        super('TerrainRouten');
        this._cfg = {
            datenUrl: 'https://kaernten.arkserver.arkturian.com/3d/daten.json',
            breite: 0.001,
            hochlage: 0.0004,
            schritt: 0.002,
            stapelAbstand: 0.0003,
            autoLoad: true,
            ...cfg,
        };
        this._terrain = cfg.terrain;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainRouten';
        this._mesh.userData.sceneNode = this;
        if (this._cfg.autoLoad) void this._lade().catch((e) => console.warn("[terrain] Standalone-Daten nicht ladbar (Embed unbeeinflusst):", e));
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    private async _lade(): Promise<void> {
        const daten = await fetch(this._cfg.datenUrl).then((r) => r.json());
        this._setRoutes(daten.routen ?? []);
    }

    /** Full-replace route feed used by MapBridge v0.3. */
    setBridgeRoutes(routes: BridgeRoute[]): void {
        this._setRoutes(routes.map(route => ({
            id: route.id,
            name: String(route.id),
            farbe: route.color ?? null,
            punkte: route.points.map(point => [point.lng, point.lat] as [number, number]),
        })));
    }

    selectRoute(routeId: number | string | null): void {
        const selected = routeId === null ? null : String(routeId);
        for (const band of this._baender) {
            const material = band.mesh.material as THREE.MeshStandardMaterial;
            material.opacity = selected !== null && String(band.daten.id) !== selected ? 0.3 : 0.92;
        }
    }

    refreshSurface(): void {
        for (const band of this._baender) {
            band.letzteHoehenSumme = NaN;
            band.drapeVersuche = 0;
        }
        this._drapeUhr = 2;
    }

    private _setRoutes(input: RouteDaten[]): void {
        this._clearRoutes();
        const proj = this._terrain.projection;
        /* Stapel-Reihenfolge deterministisch nach Routen-ID: wo Routen
         * denselben Korridor teilen, liegen die Baender UEBEREINANDER
         * statt deckungsgleich zu flimmern. */
        const routen = input
            .filter(route => route.punkte.length >= 2)
            .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
        routen.forEach((route, ebene) => {
            /* 1) geo → Welt-XZ, dann GEGLAETTET resampeln: Catmull-Rom durch
             * die rohen Stuetzen (Alex: die eckige Roh-Polyline sah als Band
             * grob-kantig aus) und aequidistant abtasten — wie das
             * applyBridgeRoutes-Muster der GLB-Karte. */
            const roh = route.punkte.map(([lng, lat]) => {
                const w = proj.geoToWorld(lng, lat, 0);
                return new THREE.Vector3(w.x, 0, w.z);
            });
            const kurve = new THREE.CatmullRomCurve3(roh, false, 'centripetal');
            const laenge = kurve.getLength();
            const anzahl = Math.max(8, Math.ceil(laenge / this._cfg.schritt));
            const xz = kurve.getSpacedPoints(anzahl).map((v) => ({ x: v.x, z: v.z }));

            const numericId = Number(route.id);
            const farbe = route.farbe || ROUTENFARBEN[numericId] || '#2fbf71';
            const material = new THREE.MeshStandardMaterial({
                color: farbe,
                roughness: 0.6,
                metalness: 0,
                transparent: true,
                opacity: 0.92,
                polygonOffset: true,
                polygonOffsetFactor: -2,
                polygonOffsetUnits: -2,
                side: THREE.DoubleSide,
            });
            const mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
            mesh.name = `route/${route.id}`;
            mesh.visible = false;
            (this._mesh as THREE.Group).add(mesh);
            this._baender.push({ daten: route, xz, mesh, letzteHoehenSumme: NaN, drapeVersuche: 0, ebene });
        });
    }

    private _clearRoutes(): void {
        for (const band of this._baender) {
            band.mesh.removeFromParent();
            band.mesh.geometry.dispose();
            const materials = Array.isArray(band.mesh.material) ? band.mesh.material : [band.mesh.material];
            for (const material of materials) material.dispose();
        }
        this._baender = [];
        this._drapeUhr = 0;
    }

    protected override onDestroy(): void {
        this._clearRoutes();
    }

    /** Drape-Laeufe: alle 2 s pruefen, ob sich die Gelaende-Hoehen unter dem
     * Band geaendert haben (feinere Kacheln geladen) — dann neu bauen. */
    /** Track-LOD: Baender aus, wenn der Track schlaeft. */
    setAktiv(aktiv: boolean): void {
        if (this._mesh) this._mesh.visible = aktiv;
    }

    protected onUpdate(deltaTime: number): void {
        this._drapeUhr += deltaTime;
        if (this._drapeUhr < 2) return;
        this._drapeUhr = 0;
        for (const band of this._baender) {
            if (band.drapeVersuche > 8) continue;
            this._drape(band);
        }
    }

    private _drape(band: Band): void {
        const hoehen: number[] = [];
        let summe = 0, fehlend = 0;
        for (const p of band.xz) {
            const y = this._terrain.getWorldY(p.x, p.z);
            if (y === null) { fehlend++; hoehen.push(0); continue; }
            hoehen.push(y);
            summe += y;
        }
        if (fehlend > band.xz.length * 0.5) return;                 // Kacheln noch nicht da
        if (Math.abs(summe - band.letzteHoehenSumme) < 0.01) return; // stabil — nichts zu tun
        band.letzteHoehenSumme = summe;
        band.drapeVersuche++;

        /* 2) Hoehe NUR auf die MITTELLINIE projizieren und entlang der Linie
         * leicht glaetten; danach Line-to-Plane OHNE erneute Projektion:
         * beide Kanten bekommen DIESELBE Hoehe → waagerechter Querschnitt
         * wie ein echter Weg, statt sich wie eine Textur schraeg an den
         * Hang zu legen (Alex 2026-08-15). */
        const n = band.xz.length;
        const geglaettet = new Float32Array(n);
        for (let i = 0; i < n; i++) {
            let s = 0, c = 0;
            for (let k = -2; k <= 2; k++) {
                const j = i + k;
                if (j < 0 || j >= n) continue;
                s += hoehen[j]; c++;
            }
            geglaettet[i] = s / c;
        }

        const halb = this._cfg.breite / 2;
        const hoch = this._cfg.hochlage + band.ebene * this._cfg.stapelAbstand;
        const positionen = new Float32Array(n * 2 * 3);
        for (let i = 0; i < n; i++) {
            const vor = band.xz[Math.max(0, i - 1)];
            const nach = band.xz[Math.min(n - 1, i + 1)];
            let ox = -(nach.z - vor.z), oz = nach.x - vor.x;
            const l = Math.hypot(ox, oz) || 1;
            ox = (ox / l) * halb; oz = (oz / l) * halb;
            const p = band.xz[i];
            const y = geglaettet[i] + hoch;
            const o = i * 6;
            positionen[o] = p.x + ox; positionen[o + 1] = y; positionen[o + 2] = p.z + oz;
            positionen[o + 3] = p.x - ox; positionen[o + 4] = y; positionen[o + 5] = p.z - oz;
        }
        const indizes = new Uint32Array((n - 1) * 6);
        let idx = 0;
        for (let i = 0; i < n - 1; i++) {
            const a = i * 2;
            indizes[idx++] = a; indizes[idx++] = a + 1; indizes[idx++] = a + 2;
            indizes[idx++] = a + 1; indizes[idx++] = a + 3; indizes[idx++] = a + 2;
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positionen, 3));
        geo.setIndex(new THREE.BufferAttribute(indizes, 1));
        geo.computeVertexNormals();
        geo.computeBoundingSphere();
        band.mesh.geometry.dispose();
        band.mesh.geometry = geo;
        band.mesh.visible = true;
    }
}
